import { createSelector } from 'reselect';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { parse } from 'query-string';
import { getSearch, push } from 'connected-react-router';
import { User as UserUtils } from 'mxp-utils';
import {
  paginationSelector,
  searchResultsLoadingSelector,
  searchResultsFetchedSelector,
  searchResultsSelector,
  skipSearchFrontloadRequestSelector,
} from 'modules/search/selectors';
import {
  fetchSearchContent,
  resetSearchModule,
  resetSkipSearchFrontloadRequest,
  setSkipUserInfoRefresh,
} from 'modules/search/actions';
import { PageCategoryAggregation } from 'components/pages/PageCategoryAggregation';
import { Props } from 'components/pages/PageCategoryAggregation/PageCategoryAggregation';
import { isAuthSelector, userShortcutsUrlsSelector } from 'modules/user/selectors';
import {
  pagePremiumContentTopicSlugSelector,
  pagePremiumContentCategorySlugSelector,
  pageCategoryTopicSlugSelector,
  pageCategorySubtopicSlugSelector,
  pageCategoryIndustrySlugSelector,
  pageCategoryTrendSlugSelector,
  pageCategorySkillSlugSelector,
  pageCategoryCategorySlugSelector,
  topicFromParamsSelectorFactory,
  subtopicFromParamsSelectorFactory,
  categoryFromParamsSelectorFactory,
  isPremiumContentPageSelector,
} from 'modules/router';
import { DefaultPagination } from 'constants/index';
import { frontloadConnect } from 'react-frontload';
import { toggleLoginReload } from 'modules/layouts';
import { refreshUserDataForPremiumContent } from 'modules/user/actions';
import { pageCategorySubskillSlugSelector, subskillFromParamsSelectorFactory } from 'modules/router/selectors';

const mapActionCreators = (dispatch: Dispatch) => ({
  async fetchSearchContent(): Promise<void> {
    await dispatch(refreshUserDataForPremiumContent());
    await dispatch(fetchSearchContent({ isGridView: true }));
  },

  resetSearchModule(): void {
    dispatch(resetSearchModule());
  },

  setSkipUserInfoRefresh(skip: boolean): void {
    dispatch(setSkipUserInfoRefresh(skip));
  },

  navigate(path: string): void {
    dispatch(push(path));
  },

  toggleLoginReload(shouldReload: boolean): void {
    dispatch(toggleLoginReload(shouldReload));
  },
  resetSkipSearchFrontloadRequest(): void {
    dispatch(resetSkipSearchFrontloadRequest());
  },
});

const mapStateToProps = () => {
  const topicFromParamsSelector = topicFromParamsSelectorFactory(pageCategoryTopicSlugSelector);
  const subtopicFromParamsSelector = subtopicFromParamsSelectorFactory(pageCategorySubtopicSlugSelector);
  const subskillFromParamsSelector = subskillFromParamsSelectorFactory(pageCategorySubtopicSlugSelector);
  const categoryFromParamsSelector = categoryFromParamsSelectorFactory(pageCategoryCategorySlugSelector);
  const pmCategoryFromParamsSelector = categoryFromParamsSelectorFactory(pagePremiumContentCategorySlugSelector);

  const userHasShortcutSelector = createSelector(
    [
      userShortcutsUrlsSelector,
      pageCategoryTopicSlugSelector,
      pageCategorySubtopicSlugSelector,
      pageCategoryIndustrySlugSelector,
      pageCategoryTrendSlugSelector,
      pageCategorySkillSlugSelector,
      pageCategorySubskillSlugSelector,
      pageCategoryCategorySlugSelector,
    ],
    (
      userShortcutsUrls: string[],
      topicSlug: string,
      subtopicSlug: string,
      industrySlug: string,
      trendSlug: string,
      skillSlug: string,
      subskillSlug: string,
      categorySlug: string
    ): boolean => {
      if (!userShortcutsUrls || !userShortcutsUrls.length) return false;
      return userShortcutsUrls.includes(
        `${categorySlug}${topicSlug}${subtopicSlug}${industrySlug}${trendSlug}${skillSlug}${subskillSlug}`
      );
    }
  );

  const premiumTopicalFromParamsSelector = (state: State.Root) => {
    const premiumTopicalSlug = pagePremiumContentTopicSlugSelector(state);
    return UserUtils.premiumContentTopicalSubItems[premiumTopicalSlug];
  };

  return (state: State.Root) => {
    const search = getSearch(state);
    const {
      sortBy = DefaultPagination.Grid.SORT_BY,
      page = DefaultPagination.Grid.PAGE,
      perPage = DefaultPagination.Grid.MIN_PER_PAGE,
      date,
      topic: searchTopic,
      subtopic: searchSubtopic,
      industry: searchIndustry,
      trend: searchTrend,
      skill: searchSkill,
      type,
      premium,
      credits,
      getNoticed,
      includedInWebcastPass,
      nasbaField,
      nasbaProgram,
      availableFormat,
      eventStartDate,
      eventEndDate,
      contentSource,
      groups,
      careerStage,
      contentLanguage,
      location,
      locationByCountry,
      locationByState,
    }: any = parse(search, {
      arrayFormat: 'comma',
      parseNumbers: true,
    });
    const cpeCredits: string[] = credits === 0 ? credits.toString() : credits;

    const filtersStr: string = [
      date,
      searchTopic,
      searchSubtopic,
      searchIndustry,
      searchTrend,
      searchSkill,
      type,
      premium,
      cpeCredits,
      getNoticed,
      includedInWebcastPass,
      nasbaField,
      nasbaProgram,
      availableFormat,
      eventStartDate,
      eventEndDate,
      contentSource,
      groups,
      careerStage,
      contentLanguage,
      location,
      locationByCountry,
      locationByState,
    ]
      .filter(Boolean)
      .join();

    const topic: State.Topic | UserUtils.PremiumContentTopicalSubItem | null = isPremiumContentPageSelector(state)
      ? premiumTopicalFromParamsSelector(state)
      : topicFromParamsSelector(state);

    const subtopic: State.Subtopic | null = subtopicFromParamsSelector(state);
    const subskill: State.Subtopic | null = subskillFromParamsSelector(state);
    const category: State.CategoriesItem | null = isPremiumContentPageSelector(state)
      ? pmCategoryFromParamsSelector(state)
      : categoryFromParamsSelector(state);

    return {
      isAuth: isAuthSelector(state),
      isSubtopicPage: Boolean(subtopic?.slug),
      isPremiumContentPage: isPremiumContentPageSelector(state),
      sortBy,
      page,
      perPage,
      filtersStr, // subscription required to fire fetchSearchContent if changed
      pagination: paginationSelector(state),
      topicId: topic?.id || '',
      topicName: topic?.name as string,
      topicSlug: pageCategoryTopicSlugSelector(state) || pagePremiumContentTopicSlugSelector(state), // source slug value required to handle not found state
      subtopicId: subtopic?.id || '',
      subtopicName: subtopic?.name || '',
      subtopicSlug: pageCategorySubtopicSlugSelector(state), // source slug value required to handle not found state,
      subskillName: subskill?.name || '',
      searchTopicSlug: searchTopic,
      industrySlug: searchIndustry,
      trendSlug: searchTrend,
      skillSlug: searchSkill,
      categorySlug: category?.slug || '',
      categoryName: category?.name || '',
      categoryDescription: category?.description || '',
      resultsLoading: searchResultsLoadingSelector(state),
      resultsFetched: searchResultsFetchedSelector(state),
      results: searchResultsSelector(state),
      hasShortcut: userHasShortcutSelector(state),
      skipSearchFrontloadRequest: skipSearchFrontloadRequestSelector(state),
    };
  };
};

const frontload = async (props: Props) => {
  if (props.skipSearchFrontloadRequest) return props.resetSkipSearchFrontloadRequest();
  return props.fetchSearchContent({ isGridView: true });
};

const options = {
  onUpdate: true,
  _experimental_updateFunc: (prevProps: Props, newProps: Props) =>
    prevProps.categorySlug !== newProps.categorySlug ||
    prevProps.topicSlug !== newProps.topicSlug ||
    prevProps.subtopicSlug !== newProps.subtopicSlug ||
    prevProps.industrySlug !== newProps.industrySlug ||
    prevProps.trendSlug !== newProps.trendSlug ||
    prevProps.skillSlug !== newProps.skillSlug ||
    prevProps.sortBy !== newProps.sortBy ||
    prevProps.page !== newProps.page ||
    prevProps.perPage !== newProps.perPage ||
    prevProps.filtersStr !== newProps.filtersStr ||
    prevProps.isAuth !== newProps.isAuth,
};

export const PageCategoryAggregationContainer = connect(
  mapStateToProps,
  mapActionCreators
)(frontloadConnect(frontload, options)(PageCategoryAggregation));
